import { UserServiceClient } from 'fitbeat.api-clients';
import { isLeaderboardUpcoming, shouldShowJoinLeaderboardTile } from 'fitbeat.gym-core';
import { IUserLeaderboardViewModel } from 'fitbeat.models';
import React, { FunctionComponent, useEffect } from 'react';
import { translate } from 'react-i18next';
import { ActivityIndicator, ScrollView, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import { getApiConfig } from '../../config';
import { GoogleAnalytics } from '../../services/google-analytics';
import Theme from '../../theme';
import { defaultScale, Logger, UserDataConsumer } from '../../utils';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { activeLeaderboardsSelector, completedLeaderboardsSelector } from '../selectors';
import { loadLeaderboardsForUser as loadLeaderboardsForUserAction } from './../actions';
import { loadInactiveLeaderboardsForUser as loadInactiveLeaderboardsForUserAction } from './../actions';
import CompletedLeaderboardsSlider from './leaderboards/CompletedLeaderboardsSlider';
import LeaderboardSectionHeading from './leaderboards/LeaderboardSectionHeading';
import { LeaderboardTile } from './LeaderboardTile';

interface IProps {
  completedLeaderboards: IUserLeaderboardViewModel[];
  activeLeaderboards: IUserLeaderboardViewModel[];
  isLoadingLeaderboards: boolean;
  isLoadingInactiveLeaderboards: boolean;
  loadLeaderboardsForUser: () => IUserLeaderboardViewModel[];
  loadInactiveLeaderboardsForUser: () => IUserLeaderboardViewModel[];
  navigation: any;
  t: (key: string, options?: any) => string;
}

export const LeaderboardHistory: FunctionComponent<IProps> =
  ({
     completedLeaderboards,
     activeLeaderboards,
     isLoadingLeaderboards,
     isLoadingInactiveLeaderboards,
     loadLeaderboardsForUser,
     loadInactiveLeaderboardsForUser,
     navigation,
     t,
   }) => {
  useEffect(() => {
    const onFocusListener = navigation.addListener('didFocus', navigationDidFocus);
    return () => {
      onFocusListener.remove();
    };
  });

  useEffect(() => {
    loadInactiveLeaderboardsForUser();
  }, []);

  useEffect(() => {
    loadLeaderboardsForUser();
  }, []);

  const navigationDidFocus = () => {
    loadLeaderboardsForUser();
    loadInactiveLeaderboardsForUser();
  }

  if (isLoadingLeaderboards) {
    return<ActivityIndicator
      size='large'
      animating
      color={Theme.PRIMARY}
      style={styles.loader}/>;
  }

  return (
    <View style={styles.container}>
        <ScrollView>
          <CompletedLeaderboardsSlider
            leaderboards={completedLeaderboards} t={t}
            onLeaderboardPress={(leaderboard) => navigation.navigate('LeaderboardStats', { leaderboard })}
            isLoadingLeaderboards={isLoadingInactiveLeaderboards}
            navigation={navigation}
          />
          <LeaderboardSectionHeading heading={t('tracking:activeLeaderboardsSectionHeading')} />
          {activeLeaderboards && activeLeaderboards.map((leaderboard: IUserLeaderboardViewModel, index: number) => {
            return (
              <LeaderboardTile
                leaderboard={leaderboard}
                key={leaderboard.leaderboardId}
                summary={true}
                t={t}
                navigation={navigation}
                onPress={() => navigation.navigate('LeaderboardStats', { leaderboard })}
                isFeatured={index === 0}
                showRankings={index === 0 && !isLeaderboardUpcoming(leaderboard.startDate)}
                showJoinLeaderboardTile={shouldShowJoinLeaderboardTile(leaderboard)}
                onTeamPress={
                  leaderboard.competitionType === 'inter-team'
                    ? (team: string) => navigation.navigate('LeaderboardTeam', { leaderboard, team })
                    : undefined
                  }
              />
            );
          })}
          {!activeLeaderboards &&
            (
              <View style={styles.noActiveLeaderboardsContainer}>
                <Text style={styles.noActiveLeaderboardText}>{t('tracking:noActiveLeaderboards')}</Text>
              </View>
            )
          }
        </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.GREY_LIGHT,
    flex: 1,
  },
  loader: {
    height: defaultScale(300),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: defaultScale(20),
    alignSelf: 'center',
  },
  noActiveLeaderboardsContainer: {
    paddingHorizontal: defaultScale(60),
    marginTop: defaultScale(20),
  },
  noActiveLeaderboardText: {
    ...Theme.textStyle.heading2,
    color: Theme.GREY_MEDIUM,
    textAlign: 'center',
  },
});

const mapStateToProps = (state: any) => {
  const { isLoadingLeaderboards, isLoadingInactiveLeaderBoards, loadingLeaderboardsError } = state.tracking.view;
  const completedLeaderboards = completedLeaderboardsSelector(state);
  const activeLeaderboards = activeLeaderboardsSelector(state);
  return {
    isLoadingLeaderboards,
    isLoadingInactiveLeaderBoards,
    loadingLeaderboardsError,
    completedLeaderboards,
    activeLeaderboards,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  const userServiceClient =
    new UserServiceClient(getApiConfig(), fetch, Logger.debug, new AuthTokenBuilder(), GoogleAnalytics);
  return {
    loadLeaderboardsForUser: () => dispatch(loadLeaderboardsForUserAction(userServiceClient)),
    loadInactiveLeaderboardsForUser: () => dispatch(
      loadInactiveLeaderboardsForUserAction(userServiceClient, 'carousel'),
    ),
  };
};

export const LeaderboardHistoryScreen = UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)
(translate(['dashboard', 'stats'], { wait: true })(LeaderboardHistory)));
